import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  Package,
  MessageSquare,
  Phone,
  Instagram,
  Bot,
  Settings,
  LayoutDashboard,
  Puzzle,
  CalendarDays,
  GraduationCap,
  Headphones,
  ChevronDown,
  Check,
  Handshake,
  ClipboardCheck,
  BarChart3,
  Megaphone,
  Zap,
} from "lucide-react";
import { useIsGeminiAdmin } from "@/hooks/useIsGeminiAdmin";

function GeminiUsageNavItem({ isCollapsed, isActive }: { isCollapsed: boolean; isActive: boolean }) {
  const { isAdmin } = useIsGeminiAdmin();
  if (!isAdmin) return null;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip="Uso de LLM">
        <NavLink to="/admin/gemini-usage" className={cn("flex items-center gap-2", isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/60")}>
          <BarChart3 className="h-4 w-4 flex-shrink-0" />
          {!isCollapsed && <span>Uso de LLM</span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ConversationSidebarIntegrationLink,
  ConversationSidebarOrphanChannelLink,
} from "@/components/layout/ConversationSidebarIntegrationLink";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { useOnboarding } from "@/contexts/OnboardingContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useUnread } from "@/contexts/UnreadContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { cn } from "@/lib/utils";
import { getPlanByProductId } from "@/lib/stripe-plans";
import { supabase } from "@/integrations/supabase/client";
import {
  sortInstagramIntegrations,
  sortMessengerIntegrations,
  sortWhatsAppIntegrations,
} from "@/lib/integration-sort";
import {
  whatsappCloudSidebarLabels,
  whatsappIntegrationDisplayLabel,
} from "@/lib/whatsapp-integration-label";
import { useSidebarConversationUnreads } from "@/hooks/useSidebarConversationUnreads";
import { useTaskDashboard } from "@/contexts/TaskDashboardContext";
import { TaskSidebarMissionStrip } from "./TaskSidebarMissionStrip";
import { TaskNavBadges, TaskNavMenuExtras, taskNavIconPulseClass } from "./TaskNavMenuExtras";
import {
  AppointmentsCalendarSubmenu,
  useAppointmentsCalendarSidebarData,
} from "@/components/appointments/AppointmentsCalendarSidebar";
import { useAppointmentsCalendarFilter } from "@/contexts/AppointmentsCalendarFilterContext";

interface MenuItem {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  gradient: string;
  color: string;
  permission?: string;
  requiresPlan?: string[];
  requiresFeature?: "disparos";
}

type ConversationChannel = "whatsapp" | "instagram" | "messenger";

interface ConversationIntegration {
  id: string;
  provider: string;
  config: Record<string, unknown> | null;
  instance_identifier: string | null;
  instagram_account_id: string | null;
  page_id: string | null;
  status: string | null;
}

const PAYWALL_EXEMPT_URLS = ["/settings", "/planos"];

const menuItems: MenuItem[] = [
  {
    title: "Tarefas",
    url: "/tasks",
    icon: ClipboardCheck,
    gradient: "from-amber-500 to-amber-600",
    color: "text-amber-500",
    permission: "tasks",
  },
  {
    title: "Conversas",
    url: "/conversations",
    icon: MessageSquare,
    gradient: "from-green-500 to-emerald-600",
    color: "text-green-500",
    permission: "conversations",
  },
  {
    title: "Agendamentos",
    url: "/appointments",
    icon: CalendarDays,
    gradient: "from-indigo-500 to-purple-600",
    color: "text-indigo-500",
    permission: "appointments",
  },
  {
    title: "Leads | CRM",
    url: "/leads",
    icon: Users,
    gradient: "from-purple-600 to-purple-700",
    color: "text-purple-600",
    permission: "crm",
  },
  {
    title: "Negociações",
    url: "/deals",
    icon: Handshake,
    gradient: "from-teal-500 to-cyan-600",
    color: "text-teal-500",
    permission: "deals",
  },
  {
    title: "Disparos",
    url: "/disparos",
    icon: Megaphone,
    gradient: "from-rose-500 to-red-600",
    color: "text-rose-500",
    permission: "automations",
    requiresFeature: "disparos",
  },
  {
    title: "Automações",
    url: "/automations",
    icon: Zap,
    gradient: "from-amber-500 to-yellow-600",
    color: "text-amber-500",
    permission: "automations",
    requiresPlan: ["intermediario", "pro"],
  },
  {
    title: "Produtos",
    url: "/products",
    icon: Package,
    gradient: "from-orange-500 to-orange-600",
    color: "text-orange-500",
    permission: "products",
  },
  {
    title: "Agentes IA",
    url: "/agents",
    icon: Bot,
    gradient: "from-pink-500 to-rose-600",
    color: "text-pink-500",
    permission: "agents",
  },
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
    gradient: "from-blue-500 to-blue-600",
    color: "text-blue-500",
    permission: "dashboard",
  },
  {
    title: "Integrações",
    url: "/integrations",
    icon: Puzzle,
    gradient: "from-indigo-500 to-indigo-600",
    color: "text-indigo-500",
    permission: "integrations",
  },
  {
    title: "Configurações",
    url: "/settings",
    icon: Settings,
    gradient: "from-gray-500 to-gray-600",
    color: "text-gray-500",
    permission: "settings",
  },
];

function getLoggedInIdentity(
  user: { email?: string; user_metadata?: Record<string, unknown> } | null,
  memberDisplayName: string | null,
): { name: string; email: string; avatarUrl: string | null } {
  const email = user?.email ?? "";
  const meta = user?.user_metadata ?? {};
  const fullName = typeof meta.full_name === "string" ? meta.full_name.trim() : "";
  const name =
    memberDisplayName?.trim() ||
    fullName ||
    (email ? email.split("@")[0] : "Usuário");
  const avatarUrl =
    (typeof meta.avatar_url === "string" && meta.avatar_url) ||
    (typeof meta.picture === "string" && meta.picture) ||
    null;
  return { name, email, avatarUrl };
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function SidebarUserAvatar({
  name,
  avatarUrl,
  className,
}: {
  name: string;
  avatarUrl: string | null;
  className?: string;
}) {
  return (
    <Avatar className={cn("shrink-0", className)}>
      {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
      <AvatarFallback className="bg-primary/15 text-primary text-[11px] font-semibold">
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

function SidebarLoggedInUser({
  user,
  memberDisplayName,
  loading,
}: {
  user: { email?: string; user_metadata?: Record<string, unknown> } | null;
  memberDisplayName: string | null;
  loading: boolean;
}) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  if (!user) return null;

  const { name, email, avatarUrl } = getLoggedInIdentity(user, memberDisplayName);
  const showEmail = Boolean(email) && name.toLowerCase() !== email.toLowerCase();

  const row = (
    <NavLink
      to="/settings"
      className={cn(
        "flex items-center gap-2.5 w-full rounded-md px-3 py-2 transition-colors",
        "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isCollapsed && "justify-center px-2",
      )}
      aria-label={`Conta: ${name}${email ? ` (${email})` : ""}`}
    >
      <SidebarUserAvatar name={loading ? "…" : name} avatarUrl={avatarUrl} className="h-8 w-8" />
      {!isCollapsed && (
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-medium leading-tight truncate text-foreground">
            {loading ? "Carregando…" : name}
          </p>
          {showEmail && (
            <p className="text-xs text-muted-foreground leading-tight truncate mt-0.5">{email}</p>
          )}
        </div>
      )}
    </NavLink>
  );

  if (isCollapsed) {
    return (
      <div className="mb-2">
        <Tooltip>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent side="right" className="max-w-[220px]">
            <p className="font-medium">{name}</p>
            {email && <p className="text-xs text-muted-foreground break-all">{email}</p>}
            <p className="text-xs text-muted-foreground mt-1">Configurações da conta</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="mb-2">
      <div className="mx-1 mb-2 border-t border-sidebar-border/70" aria-hidden />
      {row}
    </div>
  );
}

function TutorialButton() {
  const { reopenOnboarding } = useOnboarding();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <button
      onClick={reopenOnboarding}
      className={cn(
        "flex items-center gap-2 w-full rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors",
        isCollapsed && "justify-center px-2",
      )}
    >
      <GraduationCap className="h-4 w-4 flex-shrink-0" />
      {!isCollapsed && <span>Tutorial</span>}
    </button>
  );
}

function SupportButton() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const toggleSupportChat = () => {
    window.dispatchEvent(new CustomEvent("toggle-support-chat"));
  };

  return (
    <button
      onClick={toggleSupportChat}
      className={cn(
        "flex items-center gap-2 w-full rounded-md px-3 py-2 text-sm text-primary hover:bg-primary/10 transition-colors font-medium",
        isCollapsed && "justify-center px-2",
      )}
    >
      <Headphones className="h-4 w-4 flex-shrink-0" />
      {!isCollapsed && <span>Suporte</span>}
    </button>
  );
}

function getIntegrationChannel(integration: ConversationIntegration): ConversationChannel | null {
  if (integration.provider === "whatsapp_evolution" || integration.provider === "whatsapp_cloud" || integration.provider === "whatsapp_uazapi") return "whatsapp";
  if (integration.provider === "instagram") return "instagram";
  if (integration.provider === "messenger") return "messenger";
  return null;
}

function getConfigString(config: ConversationIntegration["config"], key: string): string {
  const value = config?.[key];
  return typeof value === "string" ? value : "";
}

function getIntegrationLabel(integration: ConversationIntegration): { name: string; detail: string } {
  const config = integration.config;
  if (!config && (integration.status === "disconnected" || integration.status === null)) {
    if (integration.provider === "instagram") {
      return { name: "Instagram (histórico)", detail: "Conta referenciada em conversas" };
    }
    if (integration.provider === "whatsapp_evolution" || integration.provider === "whatsapp_uazapi") {
      return { name: "WhatsApp (histórico)", detail: "Conta referenciada em conversas" };
    }
    return { name: "Messenger (histórico)", detail: "Conta referenciada em conversas" };
  }

  if (integration.provider === "whatsapp_cloud") {
    const cloudConfig = {
      friendly_name: getConfigString(config, "friendly_name") || null,
      display_phone_number: getConfigString(config, "display_phone_number") || null,
      verified_name: getConfigString(config, "verified_name") || null,
    };
    const { title, subtitle } = whatsappCloudSidebarLabels({
      instance_identifier: integration.instance_identifier,
      provider: integration.provider,
      config: cloudConfig,
    });
    return {
      name: title,
      detail: appendIntegrationStatusDetail(subtitle, integration),
    };
  }

  if (integration.provider === "whatsapp_evolution" || integration.provider === "whatsapp_uazapi") {
    const baseDetail = getConfigString(config, "phone") || getConfigString(config, "number") || getConfigString(config, "phone_number");
    return {
      name: whatsappIntegrationDisplayLabel({
        instance_identifier: integration.instance_identifier,
        provider: integration.provider,
        config: {
          friendly_name: getConfigString(config, "friendly_name") || null,
          instance_name: getConfigString(config, "instance_name") || getConfigString(config, "instanceName") || null,
          phone_number: getConfigString(config, "phone_number") || getConfigString(config, "phone") || null,
        },
      }),
      detail: appendIntegrationStatusDetail(baseDetail, integration),
    };
  }

  if (integration.provider === "instagram") {
    const instagramUsername = getConfigString(config, "instagram_username");
    const baseDetail = getConfigString(config, "instagram_name");
    return {
      name: instagramUsername ? `@${instagramUsername}` : getConfigString(config, "page_name") || "Instagram",
      detail: appendIntegrationStatusDetail(baseDetail, integration),
    };
  }

  return {
    name: getConfigString(config, "page_name") || "Messenger",
    detail: appendIntegrationStatusDetail("", integration),
  };
}

function appendIntegrationStatusDetail(detail: string, integration: ConversationIntegration): string {
  if (integration.status === "disconnected") {
    return detail.trim() ? `${detail} · Desconectado` : "Desconectado";
  }
  return detail;
}

function getChannelLabel(channel: ConversationChannel) {
  if (channel === "whatsapp") return "WhatsApp";
  if (channel === "instagram") return "Instagram";
  return "Messenger";
}

function ChannelIcon({ channel, className }: { channel: ConversationChannel; className?: string }) {
  if (channel === "whatsapp") return <Phone className={className} />;
  if (channel === "instagram") return <Instagram className={className} />;
  return <MessengerIcon className={className} />;
}

export function AppSidebar() {
  const { state, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const isCollapsed = !isMobile && state === "collapsed";
  const { user } = useAuth();
  const { workspaceId, workspaceMetadata } = useWorkspace(user?.id);
  const disparosEnabled =
    ((workspaceMetadata as { features?: { disparos?: boolean } } | null)?.features?.disparos ??
      false) === true;
  const {
    permissions,
    isAdmin: isMemberAdmin,
    loading: permissionsLoading,
    memberId,
    memberDisplayName,
  } = useMemberPermissions();
  const { hasUrgentAttention: tasksUrgent } = useTaskDashboard();
  const { unreadCount } = useUnread();
  const sidebarUnreads = useSidebarConversationUnreads(workspaceId);
  const { shouldShowPaywall, productId } = useSubscription();
  const [conversationIntegrations, setConversationIntegrations] = useState<ConversationIntegration[]>([]);
  const [historicalOrphanIntegrations, setHistoricalOrphanIntegrations] = useState<ConversationIntegration[]>([]);
  const [conversationMenuOpen, setConversationMenuOpen] = useState(false);
  const appointmentsCalendarSidebar = useAppointmentsCalendarSidebarData();
  const { isPanelExpanded: appointmentsMenuOpen, setPanelExpanded: setAppointmentsMenuOpen } =
    useAppointmentsCalendarFilter();
  const isActive = (path: string) => location.pathname === path;
  const activeConversationIntegrationId = new URLSearchParams(location.search).get("integration_id");
  const activeConversationChannel = new URLSearchParams(location.search).get("channel");

  useEffect(() => {
    if (!workspaceId) {
      setConversationIntegrations([]);
      return;
    }

    const fetchConversationIntegrations = async () => {
      const { data, error } = await supabase
        .from("integrations")
        .select(
          "id, provider, config, instance_identifier, instagram_account_id, page_id, status",
        )
        .eq("workspace_id", workspaceId)
        .in("status", ["active", "pending", "disconnected"])
        .in("provider", ["whatsapp_evolution", "whatsapp_cloud", "whatsapp_uazapi", "instagram", "messenger"]);

      if (error) {
        console.error("Error fetching conversation integrations:", error);
        return;
      }

      setConversationIntegrations((data || []) as ConversationIntegration[]);
    };

    fetchConversationIntegrations();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      setHistoricalOrphanIntegrations([]);
      return;
    }

    const loadHistoricalIntegrations = async () => {
      const { data: rows, error } = await supabase
        .from("conversations")
        .select("integration_id, channel")
        .eq("workspace_id", workspaceId)
        .not("integration_id", "is", null);

      if (error || !rows?.length) {
        setHistoricalOrphanIntegrations([]);
        return;
      }

      const firstChannelByInt = new Map<string, ConversationChannel>();
      for (const r of rows) {
        const id = r.integration_id as string | null;
        if (!id) continue;
        const ch = r.channel as string;
        if (ch !== "whatsapp" && ch !== "instagram" && ch !== "messenger") continue;
        if (!firstChannelByInt.has(id)) firstChannelByInt.set(id, ch as ConversationChannel);
      }

      const loadedIds = new Set(conversationIntegrations.map((i) => i.id));
      const missing = [...firstChannelByInt.keys()].filter((id) => !loadedIds.has(id));

      if (missing.length === 0) {
        setHistoricalOrphanIntegrations([]);
        return;
      }

      const { data: intData, error: intErr } = await supabase
        .from("integrations")
        .select("id, provider, config, instance_identifier, instagram_account_id, page_id, status")
        .in("id", missing);

      if (intErr) {
        console.error("orphan integrations:", intErr);
      }

      const fromDb = (intData || []) as ConversationIntegration[];
      const foundSet = new Set(fromDb.map((i) => i.id));
      const synthetic: ConversationIntegration[] = missing
        .filter((id) => !foundSet.has(id))
        .map((id) => {
          const ch = firstChannelByInt.get(id)!;
          return {
            id,
            provider: ch === "whatsapp" ? "whatsapp_evolution" : ch === "instagram" ? "instagram" : "messenger",
            config: null,
            instance_identifier: null,
            instagram_account_id: null,
            page_id: null,
            status: "disconnected",
          };
        });

      setHistoricalOrphanIntegrations([...fromDb, ...synthetic]);
    };

    void loadHistoricalIntegrations();
  }, [workspaceId, conversationIntegrations]);

  useEffect(() => {
    if (location.pathname === "/conversations") {
      setConversationMenuOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/appointments") {
      setAppointmentsMenuOpen(false);
    }
  }, [location.pathname, setAppointmentsMenuOpen]);

  const integrationsByChannel = useMemo(() => {
    const acc: Record<ConversationChannel, ConversationIntegration[]> = {
      whatsapp: [],
      instagram: [],
      messenger: [],
    };
    const seen = new Set<string>();
    for (const i of conversationIntegrations) {
      const ch = getIntegrationChannel(i);
      if (ch) {
        acc[ch].push(i);
        seen.add(i.id);
      }
    }
    for (const i of historicalOrphanIntegrations) {
      if (seen.has(i.id)) continue;
      const ch = getIntegrationChannel(i);
      if (ch) acc[ch].push(i);
    }
    acc.whatsapp = sortWhatsAppIntegrations(acc.whatsapp);
    acc.instagram = sortInstagramIntegrations(acc.instagram);
    acc.messenger = sortMessengerIntegrations(acc.messenger);
    return acc;
  }, [conversationIntegrations, historicalOrphanIntegrations]);

  const hasConversationSubmenu =
    conversationIntegrations.length > 0 || historicalOrphanIntegrations.length > 0;

  return (
    <Sidebar collapsible="icon" className="border-r shadow-[0_2px_12px_-2px_hsl(240_10%_15%_/_0.08)] dark:shadow-none">
      <SidebarContent>
        <SidebarGroup>
          <div
            className={cn(
              "flex items-center h-[30px]",
              isCollapsed ? "justify-center" : "justify-center w-full",
            )}
          >
            {isCollapsed ? (
              <img
                src="/icon.png?v=11"
                alt="FabChat"
                className="object-contain flex-shrink-0 transition-all h-[30px] w-24 py-[1px]"
              />
            ) : (
              <>
                <img
                  src="/logo-light.png?v=6"
                  alt="FabChat"
                  className="object-contain flex-shrink-0 transition-all block dark:hidden h-4 sm:h-[18px] w-auto"
                />
                <img
                  src="/logo.png?v=6"
                  alt="FabChat"
                  className="object-contain flex-shrink-0 transition-all hidden dark:block h-4 sm:h-[18px] w-auto"
                />
              </>
            )}
          </div>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                // Disparos: flag de workspace ou plano PRO
                if (item.requiresFeature === "disparos" && !disparosEnabled) {
                  const plan = productId ? getPlanByProductId(productId) : null;
                  if (plan !== "pro") return null;
                }

                // Gating por plano (allowlist explícita)
                if (item.requiresPlan) {
                  const plan = productId ? getPlanByProductId(productId) : null;
                  if (!plan || !item.requiresPlan.includes(plan)) {
                    return null;
                  }
                }

                // Verificar permissão do membro
                if (!permissionsLoading && !isMemberAdmin && item.permission && permissions) {
                  const hasPermission = permissions[item.permission as keyof typeof permissions];
                  if (!hasPermission) return null;
                }

                const isConversations = item.url === "/conversations";
                const hasUnread = isConversations && unreadCount > 0;
                const active = isActive(item.url);
                const isExempt = PAYWALL_EXEMPT_URLS.includes(item.url);
                const isDisabledByPaywall = shouldShowPaywall && !isExempt;

                if (isConversations && !isDisabledByPaywall && hasConversationSubmenu) {
                  const firstIntegration = conversationIntegrations[0];
                  const firstChannel = firstIntegration ? getIntegrationChannel(firstIntegration) : null;

                  if (
                    conversationIntegrations.length === 1 &&
                    historicalOrphanIntegrations.length === 0 &&
                    firstChannel
                  ) {
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          tooltip={item.title}
                          className="group/item"
                        >
                          <NavLink
                            to={`/conversations?channel=${firstChannel}&integration_id=${firstIntegration.id}`}
                            className={cn(
                              "relative transition-all duration-200 ease-out",
                              active
                                ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-medium shadow-sm"
                                : "hover:bg-muted/60 hover:shadow-sm",
                            )}
                          >
                            <div className="relative flex items-center justify-center">
                              <div className="relative">
                                {active && (
                                  <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-md blur-sm opacity-60" />
                                )}
                                <item.icon
                                  className={cn(
                                    "relative h-5 w-5 z-10 transition-all duration-200",
                                    active
                                      ? "text-primary drop-shadow-sm scale-105"
                                      : `${item.color} drop-shadow-sm group-hover/item:scale-105`,
                                  )}
                                />
                              </div>
                              {hasUnread && (
                                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background shadow-md ring-1 ring-green-500/30 animate-pulse" />
                              )}
                            </div>
                            <span className={cn("transition-all duration-200", active && "font-medium")}>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        isActive={active}
                        tooltip={item.title}
                        onClick={() => setConversationMenuOpen((open) => !open)}
                        className={cn(
                          "group/item relative transition-all duration-200 ease-out",
                          active
                            ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-medium shadow-sm"
                            : "hover:bg-muted/60 hover:shadow-sm",
                        )}
                      >
                        <div className="relative flex items-center justify-center">
                          <div className="relative">
                            {active && (
                              <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-md blur-sm opacity-60" />
                            )}
                            <item.icon
                              className={cn(
                                "relative h-5 w-5 z-10 transition-all duration-200",
                                active
                                  ? "text-primary drop-shadow-sm scale-105"
                                  : `${item.color} drop-shadow-sm group-hover/item:scale-105`,
                              )}
                            />
                          </div>
                          {hasUnread && (
                            <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background shadow-md ring-1 ring-green-500/30 animate-pulse" />
                          )}
                        </div>
                        {!isCollapsed && (
                          <>
                            <span className={cn("transition-all duration-200", active && "font-medium")}>{item.title}</span>
                            <ChevronDown
                              className={cn(
                                "ml-auto h-4 w-4 text-muted-foreground transition-transform",
                                conversationMenuOpen && "rotate-180",
                              )}
                            />
                          </>
                        )}
                      </SidebarMenuButton>

                      {conversationMenuOpen && (
                        <SidebarMenuSub className="mt-0.5 mx-2 gap-0 border-l px-1.5 py-0">
                          {(["whatsapp", "instagram", "messenger"] as ConversationChannel[]).map((channel) => {
                            const channelIntegrations = integrationsByChannel[channel];
                            const assignedUnreadSum = channelIntegrations.reduce(
                              (s, i) => s + (sidebarUnreads.byIntegration[i.id] || 0),
                              0,
                            );
                            const nullOrphanUnreads = Math.max(0, sidebarUnreads.byChannel[channel] - assignedUnreadSum);
                            if (channelIntegrations.length === 0 && nullOrphanUnreads === 0) return null;

                            const channelOnlySelected =
                              location.pathname === "/conversations" &&
                              activeConversationChannel === channel &&
                              !activeConversationIntegrationId;

                            return (
                              <div key={channel} className="[&:not(:first-child)]:mt-1.5">
                                <div className="flex items-center gap-1.5 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-normal text-muted-foreground leading-none">
                                  {getChannelLabel(channel)}
                                  {sidebarUnreads.byChannel[channel] > 0 && (
                                    <span className="ml-auto rounded-full bg-muted px-1 py-0 text-[9px] font-semibold leading-none text-muted-foreground tabular-nums">
                                      {sidebarUnreads.byChannel[channel]}
                                    </span>
                                  )}
                                </div>
                                {channelIntegrations.map((integration) => {
                                  const { name, detail } = getIntegrationLabel(integration);
                                  const isSelected = activeConversationIntegrationId === integration.id;
                                  const integrationUnreadCount = sidebarUnreads.byIntegration[integration.id] || 0;

                                  return (
                                    <ConversationSidebarIntegrationLink
                                      key={integration.id}
                                      channel={channel}
                                      integrationId={integration.id}
                                      name={name}
                                      detail={detail}
                                      isSelected={isSelected}
                                      unreadCount={integrationUnreadCount}
                                    />
                                  );
                                })}
                                {nullOrphanUnreads > 0 && (
                                  <ConversationSidebarOrphanChannelLink
                                    key={`${channel}-no-integration`}
                                    channel={channel}
                                    isSelected={channelOnlySelected}
                                    unreadCount={nullOrphanUnreads}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  );
                }

                const isTasksItem = item.url === "/tasks";
                const isAppointmentsItem = item.url === "/appointments";

                if (
                  isAppointmentsItem &&
                  !isDisabledByPaywall &&
                  appointmentsCalendarSidebar.shouldShowSubmenu &&
                  !isCollapsed
                ) {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        isActive={active}
                        tooltip={item.title}
                        onClick={() => {
                          if (!active) {
                            navigate("/appointments");
                          }
                          setAppointmentsMenuOpen((open) => !open);
                        }}
                        className={cn(
                          "group/item relative transition-all duration-200 ease-out",
                          active
                            ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-medium shadow-sm"
                            : "hover:bg-muted/60 hover:shadow-sm",
                        )}
                      >
                        <div className="relative flex items-center justify-center">
                          <div className="relative">
                            {active && (
                              <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-md blur-sm opacity-60" />
                            )}
                            <item.icon
                              className={cn(
                                "relative h-5 w-5 z-10 transition-all duration-200",
                                active
                                  ? "text-primary drop-shadow-sm scale-105"
                                  : `${item.color} drop-shadow-sm group-hover/item:scale-105`,
                              )}
                            />
                          </div>
                        </div>
                        <span className={cn("transition-all duration-200", active && "font-medium")}>
                          {item.title}
                        </span>
                        <ChevronDown
                          className={cn(
                            "ml-auto h-4 w-4 text-muted-foreground transition-transform",
                            appointmentsMenuOpen && "rotate-180",
                          )}
                        />
                      </SidebarMenuButton>

                      {appointmentsMenuOpen && <AppointmentsCalendarSubmenu />}
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem
                    key={item.title}
                    className={isTasksItem ? "flex flex-col gap-0" : undefined}
                  >
                    <SidebarMenuButton
                      asChild={!isDisabledByPaywall}
                      isActive={active}
                      tooltip={item.title}
                      className="group/item"
                    >
                      {isDisabledByPaywall ? (
                        <div className="relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left opacity-50 cursor-not-allowed select-none">
                          <div className="relative flex flex-shrink-0 items-center justify-center">
                            <item.icon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          {!isCollapsed && <span className="truncate text-muted-foreground">{item.title}</span>}
                        </div>
                      ) : (
                        <NavLink
                          to={item.url}
                          className={cn(
                            "relative transition-all duration-200 ease-out",
                            active
                              ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-medium shadow-sm"
                              : "hover:bg-muted/60 hover:shadow-sm",
                          )}
                        >
                          <div className="relative flex items-center justify-center">
                            <div className={cn("relative", isTasksItem && taskNavIconPulseClass(tasksUrgent))}>
                              {active && (
                                <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-md blur-sm opacity-60" />
                              )}
                              <item.icon
                                className={cn(
                                  "relative h-5 w-5 z-10 transition-all duration-200",
                                  active
                                    ? "text-primary drop-shadow-sm scale-105"
                                    : `${item.color} drop-shadow-sm group-hover/item:scale-105`,
                                )}
                              />
                            </div>
                            {hasUnread && (
                              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background shadow-md ring-1 ring-green-500/30 animate-pulse" />
                            )}
                          </div>
                          <span className={cn("transition-all duration-200", active && "font-medium")}>
                            {item.title}
                          </span>
                          {isTasksItem && <TaskNavBadges />}
                        </NavLink>
                      )}
                    </SidebarMenuButton>
                    {isTasksItem && !isCollapsed && !isDisabledByPaywall && <TaskNavMenuExtras />}
                  </SidebarMenuItem>
                );
              })}
              <GeminiUsageNavItem isCollapsed={isCollapsed} isActive={isActive("/admin/gemini-usage")} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="px-2 pb-4">
        {!shouldShowPaywall && <TaskSidebarMissionStrip />}
        {!shouldShowPaywall && <TutorialButton />}
        <SupportButton />
        <SidebarLoggedInUser
          user={user}
          memberDisplayName={memberDisplayName}
          loading={permissionsLoading}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
